import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import {
  getAICheckerData,
  getAICheckerEmails,
  getAICheckerUnlinkedFactuurOrders,
  getAICheckerMissingFiles,
  type AICheckerResult,
  type AICheckerEmailsResult,
  type UnlinkedFactuurOrder,
} from '../../../../../services/admin/adminService';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AI_CHECKER_RANGES, rangeToDates, type AiCheckerRange } from './ranges';
import { buildAICheckerBuckets } from './buckets';
import { AICheckerHero } from './AICheckerHero';
import { AICheckerStatsPanel } from './AICheckerStatsPanel';
import { AICheckerAllEmailsTable } from './AICheckerAllEmailsTable';
import { AICheckerKnownCasesPanel } from './knownCases/AICheckerKnownCasesPanel';
import { AICheckerQuantityFlags } from './quantityFlags/AICheckerQuantityFlags';
import { AICheckerBackfillPanel } from './panels/AICheckerBackfillPanel';
import { QuantityRecheckPanel } from './panels/QuantityRecheckPanel';
import { AICheckerExtraMenu, type ExtraPanel } from './panels/AICheckerExtraMenu';
import { AICheckerSafeSkipsPanel } from './panels/AICheckerSafeSkipsPanel';
import { TrivecRevenueCheckPanel } from './panels/TrivecRevenueCheckPanel';
import { BorkDirectApiPanel } from './panels/BorkDirectApiPanel';
import { VangelderPakbonReplayPanel } from './panels/VangelderPakbonReplayPanel';
import { VangelderRzReplayPanel } from './panels/VangelderRzReplayPanel';
import { AICheckerMismatches } from './tabs/AICheckerMismatches';
import { AICheckerUnlinkedFactuurOrders } from './tabs/AICheckerUnlinkedFactuurOrders';
import { AICheckerLineSetDivergences } from './tabs/AICheckerLineSetDivergences';
import { AICheckerMissingFiles } from './tabs/AICheckerMissingFiles';
import type { TFn } from '../types';

export function AICheckerView({ t }: { t: TFn }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceFileId = searchParams.get('source') === 'setup' ? searchParams.get('fileId') : null;
  const sourceRestaurantId = sourceFileId ? searchParams.get('restaurantId') : null;
  const requestedTab = searchParams.get('tab');
  const [account, setAccount] = useState('all');
  const [range, setRange] = useState<AiCheckerRange>(sourceFileId ? 'all' : 'week');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AICheckerResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emails, setEmails] = useState<AICheckerEmailsResult | null>(null);
  const [emailsLoading, setEmailsLoading] = useState(false);
  // Default to the full feed ("All emails"); switch to "Issues" for the score report + quantity audit list,
  // "Mismatches" for pakbon/factuur quantity diffs, "Unlinked" for orphaned factuur orders.
  // "Divergences" for same-file pakbon/factuur line-set diffs (double extraction).
  const [tab, setTab] = useState<'all' | 'issues' | 'mismatches' | 'unlinked' | 'divergences' | 'files'>(requestedTab === 'issues' || sourceFileId ? 'issues' : 'all');
  useEffect(() => {
    if (sourceFileId) { setTab('issues'); setRange('all'); setPeriodOffset(0); }
    else if (requestedTab === 'issues') setTab('issues');
  }, [sourceFileId, requestedTab]);
  // Loaded with the page, not only when the Missing-files tab is opened: source files drop out
  // whenever S3 has a wobble, so the red tab badge has to be visible on the weekly scan without
  // clicking through. The tab refreshes this same count after a repair run.
  const [missingFileCount, setMissingFileCount] = useState<number | null>(null);
  const handleMissingFileCount = useCallback((n: number) => setMissingFileCount(n), []);
  // Bump to re-fetch both the audit data and the email feed (e.g. after reprocess/reaudit/delete).
  const [reloadNonce, setReloadNonce] = useState(0);
  // Stable identity so the review buckets below can memoise on it (they build one React element per
  // issue AND per open quantity flag — now an uncapped set, so rebuilding them on every keystroke in
  // the table's search box would be felt).
  const refresh = useCallback(() => setReloadNonce((n) => n + 1), []);
  // Flag rows carry their customer question's state, so a question created from a row must show up on it.
  useEffect(() => {
    window.addEventListener('customer-requests-changed', refresh);
    return () => window.removeEventListener('customer-requests-changed', refresh);
  }, [refresh]);
  // Bumped by the "Needs attention" shortcut to force the All-emails table into its actionable preset.
  const [focusNonce, setFocusNonce] = useState(0);
  const focusNeedsAttention = () => { setTab('all'); setFocusNonce((n) => n + 1); };
  // Which backfill modal (opened from the "More" overflow menu) is showing.
  const [extraPanel, setExtraPanel] = useState<null | ExtraPanel>(null);

  // Tab 4 data lives here (not in the child) so the tab strip can badge it without opening the tab —
  // a non-zero count is ALWAYS a bug. The query is cheap: a partial index over order_history_id IS NULL.
  const [unlinked, setUnlinked] = useState<UnlinkedFactuurOrder[] | null>(null);
  const [unlinkedLoading, setUnlinkedLoading] = useState(false);
  const [unlinkedError, setUnlinkedError] = useState<string | null>(null);
  // Tab 3 stays lazy (its query scans every linked pair), so the count is only known once opened.
  const [mismatchCount, setMismatchCount] = useState<number | null>(null);
  const handleMismatchCount = useCallback((n: number) => setMismatchCount(n), []);
  // Tab 5 is lazy for the same reason; any non-zero count is a bug (same file extracted twice).
  const [divergenceCount, setDivergenceCount] = useState<number | null>(null);
  const handleDivergenceCount = useCallback((n: number) => setDivergenceCount(n), []);

  const { startDate, endDate, label: periodLabel } = rangeToDates(range, periodOffset);
  const canNavigate = range !== 'all';

  const selectRange = (r: AiCheckerRange) => {
    setRange(r);
    setPeriodOffset(0);
  };

  // Auto-load whenever the mailbox, range, or period changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await getAICheckerData(account, startDate, endDate);
        if (!cancelled) setResult(r);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load AI checker data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account, range, periodOffset, startDate, endDate, reloadNonce]);

  // Eager-load the unlinked factuur orders (Tab 4) so its badge is always accurate. Not mailbox-scoped —
  // orphaned factuur_orders are a property of stored data, not of an inbox.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setUnlinkedLoading(true);
        setUnlinkedError(null);
        const r = await getAICheckerUnlinkedFactuurOrders(startDate, endDate);
        if (!cancelled) setUnlinked(r);
      } catch (e: any) {
        if (!cancelled) setUnlinkedError(e.message || 'Failed to load unlinked factuur orders');
      } finally {
        if (!cancelled) setUnlinkedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate, reloadNonce]);

  // Eager-load the missing-source-file count (Tab 6) for the same reason: S3 outages silently drop
  // files, so the weekly review must see the badge without opening the tab. Aggregate-only query;
  // the tab itself loads the full per-supplier/per-client detail when opened.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getAICheckerMissingFiles(startDate, endDate);
        if (!cancelled) setMissingFileCount(r.missingOrders);
      } catch {
        // Badge only — a failure here must not block the rest of the page.
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate, reloadNonce]);

  // Drop the mismatch badge when the RANGE changes (the count belonged to the old range). Deliberately not
  // keyed on reloadNonce: the lazily-mounted Tab 3 does not refetch on refresh, so clearing it there would
  // blank the badge while its table still shows the old rows.
  useEffect(() => { setMismatchCount(null); }, [startDate, endDate]);

  // Load the full chronological feed only when the "All emails" tab is active.
  useEffect(() => {
    if (tab !== 'all') return;
    let cancelled = false;
    (async () => {
      try {
        setEmailsLoading(true);
        // All-time loads every row (limit 0 = no cap); dated ranges load a generous page PLUS every
        // still-open issue in the range (the server adds those on top of the cap), so the table's
        // filters and bulk actions see the same queue the hero counts — not just its newest slice.
        const r = await getAICheckerEmails(account, startDate, endDate, range === 'all' ? 0 : 2000);
        if (!cancelled) setEmails(r);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load emails');
      } finally {
        if (!cancelled) setEmailsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account, range, periodOffset, tab, startDate, endDate, reloadNonce]);

  const quantityFlags = (result?.quantityFlags ?? []).filter(flag => !sourceFileId || (flag.setupSource?.fileId === sourceFileId
    && (!sourceRestaurantId || flag.setupSource.restaurantId === sourceRestaurantId)));
  const clearSourceFilter = () => setSearchParams(current => {
    const next = new URLSearchParams(current);
    next.delete('source'); next.delete('fileId'); next.delete('restaurantId');
    return next;
  }, { replace: true });
  const stats = result?.stats;
  const accountLabel = account === 'all' ? t('adminTesting.review.accountAll') : account;
  // Open work queue (issues whose work_state is still 'open') — same buckets the score report walks through.
  const openBuckets = useMemo(() => (result ? buildAICheckerBuckets(result.issues, t, refresh) : []), [result, t, refresh]);
  // Issues-tab ring = current state: once you mark an issue done it stops counting, so the ring climbs back to
  // 100% as you clear the queue. The raw pre-intervention AI accuracy lives in the KPI tiles (from stats.withIssues).
  // Penalise BOTH email issues AND open quantity-info flags. A flag's `restaurantId` is its audit-row id, so we
  // dedupe flagged emails against the issue rows (an email left the issue queue but still has open flags must
  // still count once) — otherwise an open quantity flag wouldn't dent the score.
  const issueIds = result ? new Set(result.issues.map(i => i.id)) : new Set<string>();
  const extraFlaggedEmails = result
    ? new Set(result.quantityFlags.filter(f => !f.setupSource).map(f => f.restaurantId).filter(id => !issueIds.has(id))).size
    : 0;
  const openIssueEmails = result ? result.issues.length + extraFlaggedEmails : 0;
  const scorePct = stats && stats.total > 0 ? ((stats.total - openIssueEmails) / stats.total) * 100 : 100;
  // Average inbound email rate over the selected window. For "all", measure from the first-ever checked email;
  // otherwise from the period start. The end is capped at "now" so the current (partial) period isn't understated.
  const basisStart = range === 'all' && result?.allTime.firstCheckedAt
    ? new Date(result.allTime.firstCheckedAt)
    : new Date(`${startDate}T00:00:00`);
  const periodEndExclusive = new Date(`${endDate}T00:00:00`);
  periodEndExclusive.setDate(periodEndExclusive.getDate() + 1);
  const spanHours = Math.max(1, (Math.min(Date.now(), periodEndExclusive.getTime()) - basisStart.getTime()) / 3_600_000);
  const emailsPerHour = stats ? stats.total / spanHours : 0;

  return (
    <div className={cn(cardClass, 'px-3 py-5 sm:px-4')}>
      <div className="mb-4 flex items-start justify-between gap-4 max-lg:flex-wrap">
        <div className="min-w-0 max-w-2xl">
          <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.review.aiCheckerTitle')}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0 max-lg:flex-wrap max-lg:shrink">
          <AICheckerKnownCasesPanel account={account} startDate={startDate} endDate={endDate} reloadNonce={reloadNonce} onChanged={refresh} t={t} />
          <AICheckerSafeSkipsPanel t={t} />
          <AICheckerExtraMenu account={account} startDate={startDate} endDate={endDate} onDone={refresh} onOpenPanel={setExtraPanel} t={t} />
          {/* Controlled backfill modals — mounted here (not inside the menu) so they survive it closing. */}
          <AICheckerBackfillPanel t={t} open={extraPanel === 'backfill'} onClose={() => setExtraPanel(null)} />
          <AICheckerBackfillPanel t={t} mode="process" open={extraPanel === 'process'} onClose={() => setExtraPanel(null)} />
          <AICheckerBackfillPanel t={t} mode="candidates" open={extraPanel === 'candidates'} onClose={() => setExtraPanel(null)} />
          <QuantityRecheckPanel t={t} open={extraPanel === 'qtyRecheck'} onClose={() => setExtraPanel(null)} onDone={refresh} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2">
        {/* Mailbox */}
        <select
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          className={cn('px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize)}
        >
          <option value="all">{t('adminTesting.review.accountAll')}</option>
          <option value="cloud64">culinarycloud64@gmail.com</option>
          <option value="cloud63">culinarycloud63@gmail.com</option>
        </select>

        {/* Range presets */}
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
          {AI_CHECKER_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => selectRange(r.key)}
              className={cn('px-3 py-2 transition-colors', smallTextSize, semiBoldText,
                range === r.key
                  ? 'bg-purple-600 dark:bg-purple-600 text-white dark:text-white'
                  : cn('bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText))}
            >
              {t(r.labelKey)}
            </button>
          ))}
        </div>

        {/* Period navigation (prev / current label / next) */}
        {canNavigate && (
          <div className="inline-flex items-center gap-1">
            <button
              onClick={() => setPeriodOffset((o) => o - 1)}
              title={t('adminTesting.review.prevPeriod')}
              className={cn('px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className={cn(primaryText, smallTextSize, semiBoldText, 'min-w-[140px] text-center tabular-nums')}>
              {periodLabel}
            </span>
            <button
              onClick={() => setPeriodOffset((o) => Math.min(0, o + 1))}
              disabled={periodOffset >= 0}
              title={t('adminTesting.review.nextPeriod')}
              className={cn('px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed', primaryText)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading && <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.review.aiCheckerLoading')}</span>}
      </div>

      <div className="mt-4 space-y-4">
        <TrivecRevenueCheckPanel t={t} />
        <BorkDirectApiPanel t={t} />
        <VangelderPakbonReplayPanel t={t} />
        <VangelderRzReplayPanel t={t} />
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
      )}

      {result && stats && (
        <div className="mt-6 space-y-6">
          {stats.total === 0 ? (
            <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>
              {t('adminTesting.review.aiCheckerEmpty')}
            </div>
          ) : (
            <>
              {/* 1. Hero — period · score · spend · needs attention */}
              <AICheckerHero
                t={t}
                periodLabel={periodLabel}
                accountLabel={accountLabel}
                scorePct={scorePct}
                stats={stats}
                allTime={result.allTime}
                emailIssuesOpen={result.issues.length}
                quantityFlagsOpen={result.quantityFlags.length}
                reviewBuckets={openBuckets}
                emailsPerHour={emailsPerHour}
                onFocusNeedsAttention={focusNeedsAttention}
              />

              {/* 2. Secondary context — overview tiles + issue-type breakdown */}
              <AICheckerStatsPanel stats={stats} emailsPerHour={emailsPerHour} t={t} />
            </>
          )}

          {/* 3. Tabs: All emails (default) vs Issues (quantity-info issues list only) + quick "needs attention" jump */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Six tabs never fit a phone side by side — the pill group scrolls sideways below sm. */}
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden max-w-full max-sm:overflow-x-auto">
              {(['all', 'issues', 'mismatches', 'unlinked', 'divergences', 'files'] as const).map((tk) => {
                // Unlinked factuur orders should always be 0 — tint the tab red when they aren't.
                // Same-file line-set divergences too: a same file extracted twice is always a bug.
                const unlinkedAlarm =
                  (tk === 'unlinked' && !!unlinked && unlinked.length > 0) ||
                  (tk === 'divergences' && !!divergenceCount) ||
                  (tk === 'files' && !!missingFileCount);
                return (
                  <button
                    key={tk}
                    onClick={() => setTab(tk)}
                    className={cn('px-3 py-2 transition-colors max-sm:shrink-0 max-sm:whitespace-nowrap', smallTextSize, semiBoldText,
                      tab === tk
                        ? 'bg-purple-600 text-white'
                        : cn('bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600',
                            unlinkedAlarm ? 'text-red-600 dark:text-red-400' : primaryText))}
                  >
                    {tk === 'issues'
                      ? `${t('adminTesting.review.tabIssues')}${quantityFlags.length > 0 ? ` (${quantityFlags.length})` : ''}`
                      : tk === 'mismatches'
                      ? `${t('adminTesting.review.tabMismatches')}${mismatchCount ? ` (${mismatchCount})` : ''}`
                      : tk === 'unlinked'
                      ? `${t('adminTesting.review.tabUnlinked')}${unlinked && unlinked.length > 0 ? ` (${unlinked.length})` : ''}`
                      : tk === 'divergences'
                      ? `${t('adminTesting.review.tabDivergences')}${divergenceCount ? ` (${divergenceCount})` : ''}`
                      : tk === 'files'
                      ? `${t('adminTesting.review.tabMissingFiles')}${missingFileCount ? ` (${missingFileCount})` : ''}`
                      : t('adminTesting.review.tabAll')}
                  </button>
                );
              })}
            </div>
            {/* One-click actionable to-do list: All emails filtered to 'has issue' + 'open only'. */}
            <button
              onClick={focusNeedsAttention}
              title={t('adminTesting.review.needsAttentionFilterHint')}
              className={cn('inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors', smallTextSize, semiBoldText,
                result.issues.length > 0
                  ? 'border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30'
                  : cn('border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText))}
            >
              ⚠ {t('adminTesting.review.needsAttentionFilter')}{result.issues.length > 0 ? ` (${result.issues.length})` : ''}
            </button>
          </div>

          {tab === 'issues' && sourceFileId && <div className={cn('flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3', secondaryText, smallTextSize)}>
            <span>{t('adminTesting.review.setupUploadFilter')}</span>
            <button className="min-h-[44px] underline" onClick={clearSourceFilter}>{t('adminTesting.review.clearSetupUploadFilter')}</button>
          </div>}
          {tab === 'all' ? (
            <AICheckerAllEmailsTable data={emails} loading={emailsLoading} showAccount={account === 'all'} onChanged={refresh} focusNonce={focusNonce} t={t} />
          ) : tab === 'mismatches' ? (
            <AICheckerMismatches startDate={startDate} endDate={endDate} onCount={handleMismatchCount} t={t} />
          ) : tab === 'unlinked' ? (
            <AICheckerUnlinkedFactuurOrders orders={unlinked} loading={unlinkedLoading} error={unlinkedError} t={t} />
          ) : tab === 'divergences' ? (
            <AICheckerLineSetDivergences startDate={startDate} endDate={endDate} onCount={handleDivergenceCount} t={t} />
          ) : tab === 'files' ? (
            <AICheckerMissingFiles startDate={startDate} endDate={endDate} onCount={handleMissingFileCount} t={t} />
          ) : quantityFlags.length === 0 ? (
            <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{sourceFileId ? t('adminTesting.review.setupUploadNoOpenIssues') : <>🎉 {t('adminTesting.review.allGoodDesc')}</>}</div>
          ) : (
            <AICheckerQuantityFlags key={`${sourceRestaurantId || ''}:${sourceFileId || ''}`} flags={quantityFlags} sourceFilter={sourceFileId ? { fileId: sourceFileId, restaurantId: sourceRestaurantId || undefined } : undefined} account={account} startDate={startDate} endDate={endDate} onChanged={refresh} t={t} />
          )}
        </div>
      )}
    </div>
  );
}
